package top.wcpe.allincore.ui.runtime.element

import top.wcpe.allincore.dsl.ir.model.UiCanvasData
import top.wcpe.allincore.layout.engine.Dimension
import top.wcpe.allincore.ui.runtime.canvas.DrawCommand
import top.wcpe.allincore.ui.runtime.canvas.DrawInterpreter
import top.wcpe.allincore.ui.runtime.event.CanvasInputTarget
import top.wcpe.allincore.ui.runtime.ir.eval.EvalContext
import kotlin.jvm.JvmStatic

/**
 * canvas 元素：承载 DrawBlob 字节码，按帧解释执行得到 [DrawCommand] 列表。
 *
 * M11.2 引入时间动画支持：
 * - [computeT] 根据 `durationMs`/`loopMode` 把当前 wall-clock 毫秒映射为归一化进度 t ∈ [0,1]。
 * - [executeDrawCommands] 注入 t/now/width/height 到内层 EvalContext，供 DrawBlob 中 EXPR 操作数引用。
 * - 首次 tick 自动锁定 `startMs`；后续调用以同一基准计算进度。
 *
 * M11.7 Stage 1：width/height/duration 支持表达式。CanvasElement 持有「当前生效值」三联字段
 * （[canvasWidth]/[canvasHeight]/currentDurationMs），由 IrInterpreter 在 build 阶段注册的 Effect
 * 通过 [updateDimensions] 推送最新值；纯常量场景下 Effect 不会注册，三联字段就是 IR 中的常量。
 */
@Suppress("TooManyFunctions", "MagicNumber")
class CanvasElement private constructor(private val canvasData: UiCanvasData) :
    UIElement<CanvasElement>(),
    CanvasInputTarget {

    private val interpreter = DrawInterpreter(canvasData.drawBlob)

    /** 首次 tick 的时间戳；-1 表示尚未启动。读写仅在主线程，无需同步。 */
    private var startMs = -1L

    // M11.7 Stage 1：当前生效的尺寸 / 周期。常量路径下与 data 中的字段一致；
    // 表达式路径下由外部（IrInterpreter 注册的 Effect）通过 updateDimensions 推送。
    // 读写仅在 UI 主线程，无需同步（与 startMs 同线程契约）。
    private var currentWidth = canvasData.width
    private var currentHeight = canvasData.height
    private var currentDurationMs = canvasData.durationMs

    // M11.7 hotfix：持有 IrInterpreter 构建时的 EvalContext，便于 CanvasElementRenderer
    // 在渲染期不再传 parent 时仍能让 draw 块访问 screen 级 state（如 state.progress）。
    private var evalContextField: EvalContext? = null

    /** v4 C.1：最近一次鼠标在 canvas 局部坐标系下的 X（CanvasGraphics 偏移已减去）。 */
    private var lastMouseRelX = 0.0

    /** v4 C.1：最近一次鼠标在 canvas 局部坐标系下的 Y。 */
    private var lastMouseRelY = 0.0

    /** v4 C.1：鼠标是否当前位于 canvas 边界内；用于 DSL 表达式 `mouseInside` 引用。 */
    private var lastMouseInside = false

    /** v4 C.4：脏位标记，true 时下次 executeDrawCommands 重新调 interpreter。 */
    private var dirty = true

    /** v4 C.4：缓存的 DrawCommand 列表；duration=0 静态 canvas 首次 tick 后填充。 */
    private var cachedCommands: List<DrawCommand>? = null

    /** 最近一次生成缓存时使用的实际绘制尺寸；响应式布局变化时用于拒绝旧缓存。 */
    private var cachedDrawWidth = -1
    private var cachedDrawHeight = -1

    /**
     * v4 C.4：是否引用了 mouseX / mouseY / mouseInside（编译期注入）。
     * true 时禁用缓存（每帧 tick）；false 时静态 canvas 走缓存。
     * 简化退路：本 Stage 默认 false；后续 Stage 接入编译期扫描后由 IrInterpreter 注入 true。
     */
    private var referencesMouseInjectedField = false

    init {
        // canvas 默认尺寸由 IR 中的 width/height 决定
        styleField.layout().width = Dimension.px(canvasData.width.toFloat())
        styleField.layout().height = Dimension.px(canvasData.height.toFloat())
    }

    override fun id(id: String?): CanvasElement {
        super.id(id)
        // 把 id 同步给 DrawInterpreter，使运行时错误日志能定位到具体 canvas。
        interpreter.canvasId(id.orEmpty())
        return this
    }

    fun data(): UiCanvasData = canvasData

    fun canvasWidth(): Int = currentWidth

    fun canvasHeight(): Int = currentHeight

    /**
     * M11.7 Stage 1：把表达式求值结果回写到当前生效字段，并同步 layout 尺寸。
     * 仅由 IrInterpreter 注册的 Effect 调用；输入越界时按 u16 钳制，duration 钳到非负。
     *
     * **线程契约**：仅允许在 UI 主线程调用（与 startMs / 当前生效字段同线程），不做并发同步；
     * Effect 由 reactive 系统在主线程驱动。
     */
    fun updateDimensions(width: Int, height: Int, durationMs: Double) {
        val w = width.coerceIn(0, 0xFFFF)
        val h = height.coerceIn(0, 0xFFFF)
        currentWidth = w
        currentHeight = h
        currentDurationMs = if (durationMs.isNaN() || durationMs < 0.0) 0.0 else durationMs
        styleField.layout().width = Dimension.px(w.toFloat())
        styleField.layout().height = Dimension.px(h.toFloat())
        // v4 §C.4 缓存修正：尺寸 / duration 变化后清空绘制缓存，强制下一帧用新 size 重新执行 draw blob。
        // 否则静态 canvas（duration <= 0 且未引用 mouseX）首次 tick 后永远走旧 cachedCommands，
        // 导致外层布局已缩小但 canvas 内部图元仍按首次 size 绘制（圆环 / 玩家箭头等）。
        invalidate()
    }

    /**
     * 计算当前归一化进度 t ∈ [0, 1]。
     * - 首次调用会以 `nowMs` 锁定 [startMsOrMinusOne]。
     * - `durationMs <= 0`：始终返回 1.0（无动画）。
     * - `loopMode == null`（单次）：t = clamp((now-start)/d, 0, 1)。
     * - `loopMode == "true"`：t = ((now-start) % d) / d（循环）。
     * - `loopMode == "mirror"`：三角波（与 AnimationScheduler 镜像逻辑一致）。
     */
    fun computeT(nowMs: Long): Double {
        if (startMs < 0L) {
            startMs = nowMs
        }
        val duration = currentDurationMs
        if (duration <= 0.0) {
            return 1.0
        }
        val elapsed = (nowMs - startMs).toDouble()
        if (elapsed < 0.0) {
            return 0.0
        }
        return when (canvasData.loopMode) {
            LOOP_INFINITE -> elapsed % duration / duration
            LOOP_MIRROR -> {
                val cycles = (elapsed / duration).toLong()
                val remainder = elapsed % duration
                val phase = if (cycles % 2 == 0L) remainder else duration - remainder
                phase / duration
            }
            // null（单次）或未知 loopMode：钳制到 [0,1] 单次
            else -> (elapsed / duration).coerceAtMost(1.0)
        }
    }

    /** 兼容入口：不带时间，t=0、now=0。仅作占位/测试用。 */
    fun executeDrawCommands(): List<DrawCommand> = interpreter.execute(0.0, 0L, canvasWidth(), canvasHeight(), null)

    /** 推荐入口：根据当前 wall-clock 毫秒 tick 一次。若已注入 EvalContext 则一并传入。 */
    fun executeDrawCommands(nowMs: Long): List<DrawCommand> =
        executeDrawCommands(nowMs, evalContextField, canvasWidth(), canvasHeight())

    /** 响应式入口：使用布局引擎本帧实测的 canvas 尺寸执行绘制。 */
    fun executeDrawCommands(nowMs: Long, drawWidth: Int, drawHeight: Int): List<DrawCommand> =
        executeDrawCommands(nowMs, evalContextField, drawWidth, drawHeight)

    /**
     * M11.7 hotfix：由 IrInterpreter 在构建期把当前 screen 的 EvalContext 注入到 canvas，
     * 后续 CanvasElementRenderer 每帧渲染时无需再显式传 parent。
     */
    fun evalContext(ctx: EvalContext?) {
        evalContextField = ctx
    }

    /**
     * v4 C.1：由 InputDispatcher.dispatchMouseMove 在命中本 canvas 时调用，缓存鼠标局部坐标 + inside 标记。
     * 未命中时调用方传入最后坐标 + inside=false，用于 DSL 退出动画继续读到坐标但条件式可识别「已离开」。
     *
     * 线程契约：仅在 UI 主线程调用（与 InputDispatcher 同线程）。
     */
    override fun mousePosition(relX: Double, relY: Double, inside: Boolean) {
        lastMouseRelX = relX
        lastMouseRelY = relY
        lastMouseInside = inside
    }

    /** v4 C.4：编译期注入 mouseX/Y/Inside 引用标志（IrInterpreter 在构建期调用）。 */
    fun referencesMouseInjected(v: Boolean) {
        referencesMouseInjectedField = v
        // 标志变化时清缓存，避免脏数据
        invalidate()
    }

    /** v4 C.4：标记 canvas 需要重绘。可由响应式系统在 state 变化时触发；目前仅测试与子类调用。 */
    fun invalidate() {
        dirty = true
        cachedCommands = null
        cachedDrawWidth = -1
        cachedDrawHeight = -1
    }

    /** v4 C.1：测试与 InputDispatcher 用，回读最后注入的鼠标局部坐标 X。 */
    override fun lastMouseX(): Double = lastMouseRelX

    /** v4 C.1：测试与 InputDispatcher 用，回读最后注入的鼠标局部坐标 Y。 */
    override fun lastMouseY(): Double = lastMouseRelY

    /** v4 C.1：测试与 InputDispatcher 用，回读鼠标是否当前位于本 canvas 内。 */
    fun lastMouseInside(): Boolean = lastMouseInside

    /**
     * 完整入口：根据 `nowMs` 计算 t，并把 t/now/width/height 以及 v4 C.1 的
     * mouseX / mouseY / mouseInside 注入 EvalContext 后求值所有 EXPR 操作数。
     *
     * @param parent 父 EvalContext；可为 null（不需要访问外层 state/theme 时）
     */
    fun executeDrawCommands(nowMs: Long, parent: EvalContext?): List<DrawCommand> =
        executeDrawCommands(nowMs, parent, canvasWidth(), canvasHeight())

    private fun executeDrawCommands(
        nowMs: Long,
        parent: EvalContext?,
        drawWidth: Int,
        drawHeight: Int,
    ): List<DrawCommand> {
        val t = computeT(nowMs)
        // 动画、鼠标或任意 EXPR 操作数都可能逐帧变化；仅纯字面量静态 canvas 允许缓存。
        val width = drawWidth.coerceIn(0, 0xFFFF)
        val height = drawHeight.coerceIn(0, 0xFFFF)
        val shouldCache = currentDurationMs <= 0.0 &&
            !referencesMouseInjectedField &&
            !interpreter.hasDynamicExpressions()
        reusableCache(shouldCache, width, height)?.let { return it }
        val result = interpreter.execute(
            t, nowMs, width, height,
            lastMouseRelX,
            lastMouseRelY,
            lastMouseInside,
            parent,
        )
        updateCache(result, shouldCache, width, height)
        return result
    }

    private fun reusableCache(shouldCache: Boolean, width: Int, height: Int): List<DrawCommand>? {
        if (!shouldCache || dirty || cachedDrawWidth != width || cachedDrawHeight != height) return null
        return cachedCommands
    }

    private fun updateCache(result: List<DrawCommand>, shouldCache: Boolean, width: Int, height: Int) {
        cachedCommands = if (shouldCache) result else null
        cachedDrawWidth = if (shouldCache) width else -1
        cachedDrawHeight = if (shouldCache) height else -1
        dirty = !shouldCache
    }

    /** 测试辅助：暴露当前 startMs，未启动返回 -1。 */
    fun startMsOrMinusOne(): Long = startMs

    // canvas 是叶子，强制返回空列表
    override fun children(): List<UIElement<*>> = emptyList()

    override fun self(): CanvasElement = this

    companion object {
        private const val LOOP_INFINITE = "true"
        private const val LOOP_MIRROR = "mirror"

        @JvmStatic
        fun create(data: UiCanvasData): CanvasElement = CanvasElement(data)

        /** 用空数据创建占位 canvas（仅工厂回退用）。 */
        @JvmStatic
        fun empty(): CanvasElement = CanvasElement(UiCanvasData(0, 0, byteArrayOf(0, 0)))
    }
}
